import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PreLaunchBanner from "@/components/redeem/PreLaunchBanner";
import RateTicker from "@/components/redeem/RateTicker";
import SessionState from "@/components/redeem/SessionState";
import RecentRedemptions from "@/components/redeem/RecentRedemptions";
import WalletCard from "@/components/account/WalletCard";
import AccountAuthStrip from "@/components/account/AccountAuthStrip";
import RedemptionSummary from "@/components/account/RedemptionSummary";
import { getAuthMe, postLogout } from "@/lib/api";

/**
 * AP-5A.1 — Read-only `/account` shell.
 *
 * Single-page consolidation of the existing AssosPay surface for a
 * logged-in user. Reuses RateTicker / SessionState / RecentRedemptions
 * / PreLaunchBanner verbatim from the redeem flow. Strictly read-only:
 * no submit, no /credit, no wallet signature, no on-chain action.
 *
 * Shadow-mode rule: while ARC contract is not deployed, the balance
 * panel renders "0 ARC" with a "Pre-launch / contract not deployed"
 * badge. Never displays a seeded or assumed balance.
 *
 * Wallet card (S2/S3) and active-pending list (S7) are deferred to
 * AP-5A.2 and AP-5A.3 respectively per the AP-5A plan.
 */
export default function Account() {
  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);
  const [walletSnapshot, setWalletSnapshot] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const out = await getAuthMe();
        if (alive) setMe(out);
      } catch (_e) {
        // 401 is the normal "signed out" path on this page.
        if (alive) setMe(null);
      } finally {
        if (alive) setMeLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const linked = !!me?.arc_user_id;

  const handleLogout = async () => {
    try {
      await postLogout();
    } catch (_) {
      // Server is idempotent — proceed with local clear on any error.
    }
    setMe(null);
    try {
      const refreshed = await getAuthMe();
      setMe(refreshed);
    } catch (_) {
      setMe(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="account-page">
      <Header />

      <div className="relative">
        <div className="spotlight absolute inset-0 -z-0 pointer-events-none" />

        <main
          className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
          data-testid="account-main"
        >
          {/* ---- Page heading ---- */}
          <div className="mb-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF]">
              AssosPay · ArenaCoin
            </p>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-black text-white">
              Your account
            </h1>
            <p className="mt-2 font-mono text-xs text-white/60 max-w-2xl">
              Read-only overview of your AssosPay session, the ARC
              ecosystem rate, your pre-launch ARC balance state, your
              portfolio, and your recent redemptions.
            </p>
          </div>

          {/* ---- Pre-launch banner ---- */}
          <div className="mb-6">
            <PreLaunchBanner />
          </div>

          {/* ---- Top row: rate + session ---- */}
          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <RateTicker />
            <SessionState me={me} loading={meLoading} onLogout={handleLogout} />
          </div>

          {/* ---- Pre-launch ARC balance + wallet panel ---- */}
          <div className="mb-6">
            <WalletCard
              linked={linked}
              sessionLoading={meLoading}
              onDataChange={setWalletSnapshot}
            />
          </div>

          {/* ---- AP-5B.3 auth-method + verification strip ---- */}
          {linked && (
            <div className="mb-6">
              <AccountAuthStrip
                authMethods={me?.auth_methods || []}
                walletConnected={!!walletSnapshot?.wallet_connected}
                verificationStatus={walletSnapshot?.verification_status || null}
              />
            </div>
          )}

          {/* ---- Portfolio summary: counters + active/pending list ---- */}
          {linked && (
            <div className="mb-6">
              <RedemptionSummary linked={linked} />
            </div>
          )}

          {/* ---- Recent redemptions ---- */}
          <div className="mb-6">
            <RecentRedemptions linked={linked} />
          </div>

          {/* ---- Redeem CTA ---- */}
          <div className="mb-10">
            <RedeemCta linked={linked} />
          </div>

          {/* ---- Footnote ---- */}
          <div
            data-testid="account-help-copy"
            className="rounded-md border border-white/5 bg-black/40 p-5"
          >
            <h3 className="font-display text-xs uppercase tracking-[0.25em] text-white/85">
              About this page
            </h3>
            <ul className="mt-3 space-y-2 font-mono text-[11px] leading-relaxed text-white/55 list-disc pl-5">
              <li>
                AssosPay never holds your private keys, never moves ARC
                on your behalf, and never mutates your Assos365 balance
                directly.
              </li>
              <li>
                Until the ARC contract is deployed, the on-chain
                balance is intentionally shown as zero. A connected
                external wallet (if any) is informational only.
              </li>
              <li>
                Redemptions are submitted from the dedicated{" "}
                <Link
                  to="/redeem"
                  className="text-[#00F0FF] hover:text-[#39FF14] transition-colors"
                  data-testid="account-help-redeem-link"
                >
                  redeem
                </Link>{" "}
                page. This account page is read-only.
              </li>
            </ul>
            <p className="mt-3 font-mono text-[10px] text-white/40">
              AP-5A.3 · account portfolio · read-only
            </p>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}

/**
 * CTA into /redeem. Always renders (gating is on the redeem page
 * itself), but the copy adapts to the link state to avoid sending an
 * unlinked user into a flow they cannot complete.
 */
function RedeemCta({ linked }) {
  return (
    <Link
      to="/redeem"
      data-testid="account-redeem-cta"
      data-linked={linked ? "true" : "false"}
      className="group flex items-center justify-between gap-4 rounded-md border border-[#39FF14]/30 bg-[#39FF14]/5 px-5 py-4 hover:border-[#39FF14]/60 hover:bg-[#39FF14]/10 transition-colors"
    >
      <div>
        <p className="font-display text-sm uppercase tracking-[0.2em] text-white">
          Redeem ARC for Assos365 credit
        </p>
        <p className="mt-1 font-mono text-[11px] text-white/60">
          {linked
            ? "Continue to the redeem page to submit a new redemption."
            : "Sign in with Assos365 first, then submit a redemption."}
        </p>
      </div>
      <ArrowRight className="h-5 w-5 text-[#39FF14] group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}
